import {
  Alignment,
  AnchorButton,
  Button,
  Checkbox,
  Icon,
  Intent,
  Menu,
  PopoverInteractionKind,
} from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import { MenuItem2, Popover2 } from '@blueprintjs/popover2';
import { ItemRenderer, QueryListRendererProps } from '@blueprintjs/select';
import classNames from 'classnames';
import * as React from 'react';
import { isEqual, noop } from 'vet-bones/utils';

import { Outbound } from 'src/components/icons/Outbound';
import { ItemQueryList } from 'src/components/select/ItemQueryList';
import {
  TEST_IDS,
  getProjectMappingFilterMenuItemId,
} from 'src/constants/testIds';
import { Mapping } from 'src/containers/ProjectDiagram/types/data';
import { getModelConceptClassValue } from 'src/containers/ProjectDiagram/utils/modelConceptClass/createModelConceptClass';
import { useMappingFromRoute } from 'src/containers/ProjectMapping/hooks/useMappingFromRoute';
import { MappingFilterItem } from 'src/containers/ProjectMapping/types';
import {
  projectMappingAttributeFilterItemsSelector,
  projectMappingAttributeFilterSetsSelector,
  projectMappingAttributeFiltersSelector,
} from 'src/selectors/projectMappingAttributeFilters';
import {
  projectMappingRelationshipFilterItemsSelector,
  projectMappingRelationshipFiltersSelector,
  projectMappingRelationshipsFilterSetsSelector,
} from 'src/selectors/projectMappingRelationshipFilters';
import {
  MappingFilterCategory,
  MappingFilterCheckState,
  MappingFilterType,
} from 'src/store/ui/constants';
import { uiSlice } from 'src/store/ui/ui.slice';
import * as copy from 'src/templates/copy';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';

const SubMenu = ItemQueryList.ofType<MappingFilterItem>();

const getVisibleIris = (query: string, items: MappingFilterItem[]) => {
  const lowerCaseQuery = query.toLocaleLowerCase();
  return new Set(
    items
      .filter(
        ({ text }) => text.toLocaleLowerCase().search(lowerCaseQuery) !== -1
      )
      .map(({ value }) => value)
  );
};

export const ProjectClassMappingHeaderFilterMenu: React.VFC = () => {
  const { projectId } = useRouterParams();
  const dispatch = useReduxDispatch();
  const mapping = useMappingFromRoute() as Mapping & { to: string };

  const [isAttributeMenuOpen, setIsAttributeMenuOpen] =
    React.useState<boolean>(false);
  const [isRelationshipMenuOpen, setIsRelationshipMenuOpen] =
    React.useState<boolean>(false);

  const { allAttributes } = useReduxSelector(
    projectMappingAttributeFilterSetsSelector(projectId, mapping.value)
  );
  const { allRelationships } = useReduxSelector(
    projectMappingRelationshipsFilterSetsSelector(
      projectId,
      getModelConceptClassValue(mapping.to),
      mapping.to
    )
  );

  const attributeFilterState = useReduxSelector(
    projectMappingAttributeFiltersSelector(projectId, mapping.value)
  );
  const relationshipFilterState = useReduxSelector(
    projectMappingRelationshipFiltersSelector(
      projectId,
      getModelConceptClassValue(mapping.to),
      mapping.value
    )
  );

  const { allAttributeItems, directAttributeItems, inheritedAttributeItems } =
    useReduxSelector(
      projectMappingAttributeFilterItemsSelector(projectId, mapping.value)
    );
  const {
    allRelationshipItems,
    directRelationshipItems,
    inheritedRelationshipItems,
  } = useReduxSelector(
    projectMappingRelationshipFilterItemsSelector(
      projectId,
      getModelConceptClassValue(mapping.to),
      mapping.value
    )
  );
  const [visibleAttributes, setVisibleAttributes] = React.useState<Set<string>>(
    new Set(allAttributes)
  );
  const [visibleRelationships, setVisibleRelationships] = React.useState<
    Set<string>
  >(new Set(allRelationships));

  const getItemCategory = React.useCallback(
    (item: MappingFilterItem) => {
      if (allAttributeItems.includes(item)) {
        return MappingFilterCategory.ATTRIBUTE;
      }
      if (allRelationshipItems.includes(item)) {
        return MappingFilterCategory.RELATIONSHIP;
      }
      return null;
    },
    [allAttributeItems, allRelationshipItems]
  );

  const toggleFilters = React.useCallback(
    (itemsToFilter: MappingFilterItem[], checked?: boolean) => {
      dispatch(
        uiSlice.actions.updatedMappingFilters({
          projectId,
          mappingId: mapping.value,
          filtersByIri: itemsToFilter.reduce(
            (agg, item) => ({
              ...agg,
              [item.value]:
                checked === undefined
                  ? item.checkState !== MappingFilterCheckState.CHECKED
                  : checked,
            }),
            {}
          ),
        })
      );
    },
    [dispatch, mapping, projectId]
  );

  const handleToggleItem = React.useCallback(
    (item: MappingFilterItem) => {
      const itemCategory = getItemCategory(item);
      if (itemCategory === MappingFilterCategory.ATTRIBUTE) {
        if (item.value === MappingFilterType.DIRECT_ITEMS) {
          toggleFilters(
            directAttributeItems,
            attributeFilterState.DIRECT_ITEMS !==
              MappingFilterCheckState.CHECKED
          );
        } else if (item.value === MappingFilterType.INHERITED_ITEMS) {
          toggleFilters(
            inheritedAttributeItems,
            attributeFilterState.INHERITED_ITEMS !==
              MappingFilterCheckState.CHECKED
          );
        } else {
          toggleFilters([item]);
        }
      }
      if (itemCategory === MappingFilterCategory.RELATIONSHIP) {
        if (item.value === MappingFilterType.DIRECT_ITEMS) {
          toggleFilters(
            directRelationshipItems,
            relationshipFilterState.DIRECT_ITEMS !==
              MappingFilterCheckState.CHECKED
          );
        } else if (item.value === MappingFilterType.INHERITED_ITEMS) {
          toggleFilters(
            inheritedRelationshipItems,
            relationshipFilterState.INHERITED_ITEMS !==
              MappingFilterCheckState.CHECKED
          );
        } else {
          toggleFilters([item]);
        }
      }
    },
    [
      getItemCategory,
      toggleFilters,
      directAttributeItems,
      attributeFilterState.DIRECT_ITEMS,
      attributeFilterState.INHERITED_ITEMS,
      inheritedAttributeItems,
      directRelationshipItems,
      relationshipFilterState.DIRECT_ITEMS,
      relationshipFilterState.INHERITED_ITEMS,
      inheritedRelationshipItems,
    ]
  );

  const handleToggleVisibleItems = React.useCallback(
    (checked: boolean, category: MappingFilterCategory) => () => {
      if (category === MappingFilterCategory.ATTRIBUTE) {
        toggleFilters(
          [
            ...directAttributeItems.filter(({ value }) =>
              visibleAttributes.has(value)
            ),
            ...inheritedAttributeItems.filter(({ value }) =>
              visibleAttributes.has(value)
            ),
          ],
          checked
        );
      }
      if (category === MappingFilterCategory.RELATIONSHIP) {
        toggleFilters(
          [
            ...directRelationshipItems.filter(({ value }) =>
              visibleRelationships.has(value)
            ),
            ...inheritedRelationshipItems.filter(({ value }) =>
              visibleRelationships.has(value)
            ),
          ],
          checked
        );
      }
    },
    [
      toggleFilters,
      directAttributeItems,
      inheritedAttributeItems,
      visibleAttributes,
      directRelationshipItems,
      inheritedRelationshipItems,
      visibleRelationships,
    ]
  );

  const onQueryChange = React.useCallback(
    (query: string) => {
      const newVisibleAttributes = getVisibleIris(query, [
        ...directAttributeItems,
        ...inheritedAttributeItems,
      ]);
      const newVisibleRelationships = getVisibleIris(query, [
        ...directRelationshipItems,
        ...inheritedRelationshipItems,
      ]);
      if (!isEqual(visibleAttributes, newVisibleAttributes)) {
        setVisibleAttributes(newVisibleAttributes);
      }
      if (!isEqual(visibleRelationships, newVisibleRelationships)) {
        setVisibleRelationships(newVisibleRelationships);
      }
    },
    [
      directAttributeItems,
      directRelationshipItems,
      inheritedAttributeItems,
      inheritedRelationshipItems,
      visibleAttributes,
      visibleRelationships,
    ]
  );

  const itemListPredicate = React.useCallback(
    (query: string, items: MappingFilterItem[]) => {
      const visibleDirectAttributeIris = getVisibleIris(
        query,
        directAttributeItems
      );
      const visibleInheritedAttributeIris = getVisibleIris(
        query,
        inheritedAttributeItems
      );
      const visibleDirectRelationshipIris = getVisibleIris(
        query,
        directRelationshipItems
      );
      const visibleInheritedRelationshipIris = getVisibleIris(
        query,
        inheritedRelationshipItems
      );
      return items.filter((item) => {
        if (item.value === MappingFilterType.DIRECT_ITEMS) {
          return item.text === 'Relationships'
            ? visibleDirectRelationshipIris.size
            : visibleDirectAttributeIris.size;
        }
        if (item.value === MappingFilterType.INHERITED_ITEMS) {
          return item.text === 'InheritedRelationships'
            ? visibleInheritedRelationshipIris.size
            : visibleInheritedAttributeIris.size;
        }
        return (
          visibleDirectAttributeIris.has(item.value) ||
          visibleInheritedAttributeIris.has(item.value) ||
          visibleDirectRelationshipIris.has(item.value) ||
          visibleInheritedRelationshipIris.has(item.value)
        );
      });
    },
    [
      directAttributeItems,
      directRelationshipItems,
      inheritedAttributeItems,
      inheritedRelationshipItems,
    ]
  );

  const subMenuRenderer = React.useCallback(
    (
      { itemList }: QueryListRendererProps<MappingFilterItem>,
      queryInputElement: JSX.Element
    ) => {
      return (
        <>
          {queryInputElement}
          <div className="sd-class-mapping-filter-menu__shortcuts">
            <AnchorButton
              data-testid={TEST_IDS.projectMapping.filterMenu.selectAllButton}
              intent={Intent.PRIMARY}
              minimal
              onClick={handleToggleVisibleItems(
                true,
                queryInputElement.props.className
              )}
              small
              text={copy.components.projectMapping.filter.selectAll}
            />
            |
            <AnchorButton
              data-testid={TEST_IDS.projectMapping.filterMenu.clearButton}
              intent={Intent.PRIMARY}
              minimal
              onClick={handleToggleVisibleItems(
                false,
                queryInputElement.props.className
              )}
              small
              text={copy.components.projectMapping.filter.clear}
            />
          </div>
          {itemList}
        </>
      );
    },
    [handleToggleVisibleItems]
  );

  const subMenuItemRenderer = React.useCallback<
    ItemRenderer<MappingFilterItem>
  >(
    (item, itemProps) => {
      if (!itemProps.modifiers.matchesPredicate) {
        return null;
      }
      let hideCheckbox = false;
      const isHeader =
        item.value === MappingFilterType.DIRECT_ITEMS ||
        item.value === MappingFilterType.INHERITED_ITEMS;

      // hide the checkbox for header row when filtered
      const itemName = item.text.toLowerCase();
      if (
        isHeader &&
        itemName.includes(MappingFilterCategory.RELATIONSHIP.toLowerCase())
      ) {
        hideCheckbox =
          visibleAttributes.size !== allAttributes.size && isHeader;
      }
      if (
        isHeader &&
        itemName.includes(MappingFilterCategory.ATTRIBUTE.toLowerCase())
      ) {
        hideCheckbox =
          visibleRelationships.size !== allRelationships.size && isHeader;
      }
      return (
        <MenuItem2
          key={item.value}
          className={classNames('sd-class-mapping-filter-menu__leaf', {
            'sd-heading': isHeader,
            'sd-class-mapping-filter-menu__leaf--hidden-checkbox': hideCheckbox,
          })}
          data-testid={getProjectMappingFilterMenuItemId(
            item.value,
            itemProps.index
          )}
          disabled={hideCheckbox}
          shouldDismissPopover={false}
          text={
            <Checkbox
              alignIndicator={Alignment.RIGHT}
              checked={item.checkState === MappingFilterCheckState.CHECKED}
              disabled={hideCheckbox}
              indeterminate={
                item.checkState === MappingFilterCheckState.INDETERMINATE
              }
              labelElement={
                <span className="sd-class-mapping-filter-menu__leaf__label">
                  {item.text}
                </span>
              }
              onChange={() => {
                handleToggleItem(item);
              }}
            />
          }
        />
      );
    },
    [
      allAttributes.size,
      allRelationships.size,
      handleToggleItem,
      visibleAttributes.size,
      visibleRelationships.size,
    ]
  );

  return (
    <Popover2
      content={
        <Menu className="sd-class-mapping-filter-menu">
          {/* Relationship Menu Section*/}
          <MenuItem2
            data-testid={TEST_IDS.projectMapping.filterMenu.relationshipMenu}
            /*
              Extension of the CLICK default to prevent annoying interactions
              when the menu resizes while filtering: Open the submenu on click
              or hover, but don't hide when the cursor leaves
            */
            onMouseEnter={() => {
              setIsAttributeMenuOpen(false);
              setIsRelationshipMenuOpen(true);
            }}
            popoverProps={{
              interactionKind: PopoverInteractionKind.CLICK,
              isOpen: isRelationshipMenuOpen,
              onInteraction: (state) => setIsRelationshipMenuOpen(state),
              popoverClassName: 'sd-class-mapping-filter-menu__submenu',
            }}
            shouldDismissPopover={false}
            text={
              <Checkbox
                checked={
                  relationshipFilterState.ALL_ITEMS ===
                  MappingFilterCheckState.CHECKED
                }
                disabled={!allRelationships.size}
                indeterminate={
                  relationshipFilterState.ALL_ITEMS ===
                  MappingFilterCheckState.INDETERMINATE
                }
                labelElement={
                  <>
                    <Icon
                      className="sd-class-mapping-filter-menu__root__icon"
                      icon={<Outbound />}
                    />
                    {copy.components.projectMapping.filter.directRelationships}
                  </>
                }
                onChange={() =>
                  toggleFilters(
                    [...directRelationshipItems, ...inheritedRelationshipItems],
                    relationshipFilterState.ALL_ITEMS !==
                      MappingFilterCheckState.CHECKED
                  )
                }
              />
            }
          >
            {allRelationships.size ? (
              <SubMenu
                customRenderer={subMenuRenderer}
                inputProps={{
                  testId: TEST_IDS.projectMapping.filterMenu.filterField,
                  className: MappingFilterCategory.RELATIONSHIP,
                }}
                itemListPredicate={itemListPredicate}
                itemRenderer={subMenuItemRenderer}
                items={allRelationshipItems}
                onQueryChange={onQueryChange}
                onSelect={noop}
              />
            ) : null}
          </MenuItem2>
          {/* Attribute Menu section */}
          <MenuItem2
            data-testid={TEST_IDS.projectMapping.filterMenu.attributeMenu}
            /*
              Extension of the CLICK default to prevent annoying interactions
              when the menu resizes while filtering: Open the submenu on click
              or hover, but don't hide when the cursor leaves
            */
            onMouseEnter={() => {
              setIsRelationshipMenuOpen(false);
              setIsAttributeMenuOpen(true);
            }}
            popoverProps={{
              interactionKind: PopoverInteractionKind.CLICK,
              isOpen: isAttributeMenuOpen,
              onInteraction: (state) => setIsAttributeMenuOpen(state),
              popoverClassName: 'sd-class-mapping-filter-menu__submenu',
            }}
            shouldDismissPopover={false}
            text={
              <Checkbox
                checked={
                  attributeFilterState.ALL_ITEMS ===
                  MappingFilterCheckState.CHECKED
                }
                disabled={!allAttributes.size}
                indeterminate={
                  attributeFilterState.ALL_ITEMS ===
                  MappingFilterCheckState.INDETERMINATE
                }
                labelElement={
                  <>
                    <Icon
                      className="sd-class-mapping-filter-menu__root__icon"
                      icon={IconNames.STOP}
                    />
                    {copy.components.projectMapping.filter.directAttributes}
                  </>
                }
                onChange={() =>
                  toggleFilters(
                    [...directAttributeItems, ...inheritedAttributeItems],
                    attributeFilterState.ALL_ITEMS !==
                      MappingFilterCheckState.CHECKED
                  )
                }
              />
            }
          >
            {allAttributes.size ? (
              <SubMenu
                customRenderer={subMenuRenderer}
                inputProps={{
                  testId: TEST_IDS.projectMapping.filterMenu.filterField,
                  className: MappingFilterCategory.ATTRIBUTE,
                }}
                itemListPredicate={itemListPredicate}
                itemRenderer={subMenuItemRenderer}
                items={allAttributeItems}
                onQueryChange={onQueryChange}
                onSelect={noop}
              />
            ) : null}
          </MenuItem2>
        </Menu>
      }
    >
      <Button
        aria-label={copy.components.projectMapping.filter.iconLabel}
        className={classNames({
          'sd-project-mapping-header__filter-button--muted':
            attributeFilterState.ALL_ITEMS ===
              MappingFilterCheckState.CHECKED &&
            relationshipFilterState.ALL_ITEMS ===
              MappingFilterCheckState.CHECKED,
        })}
        data-testid={TEST_IDS.projectMapping.filterMenu.filterButton}
        icon={IconNames.FILTER}
        intent={
          attributeFilterState.ALL_ITEMS === MappingFilterCheckState.CHECKED &&
          relationshipFilterState.ALL_ITEMS === MappingFilterCheckState.CHECKED
            ? Intent.NONE
            : Intent.PRIMARY
        }
        minimal
      />
    </Popover2>
  );
};
